import { Component } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { PermissionService } from '../../permissions.service';

@Component({
  selector: 'sidebar',
  standalone: true,
  imports: [RouterModule, CommonModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent {
  currentUrl = '';

  // Booleans to track permissions
  canViewHero = false;           // corresponds to “EVENTS”
  canViewUsers = false;          // corresponds to “USERS”
  canViewOrganizations = false;  // corresponds to “ORGANIZATIONS”

  // Ordered array of permitted “labels”
  options: string[] = [];

  constructor(
    private router: Router,
    private permissionService: PermissionService
  ) {
    // Check permissions once
    this.canViewHero = this.permissionService.can('view:hero');
    this.canViewUsers = this.permissionService.can('view:users');
    this.canViewOrganizations = this.permissionService.can('view:organizations');

    // Build up `options` in the priority order you want
    if (this.canViewHero) {
      this.options.push('EVENTS');
    }
    if (this.canViewUsers) {
      this.options.push('USERS');
    }
    if (this.canViewOrganizations) {
      this.options.push('ORGANIZATIONS');
    }

    // Listen to navigation events to capture the final URL
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.urlAfterRedirects;
        this.handleRootRedirect();
      });

    // Also handle the initial load
    this.currentUrl = this.router.url;
    this.handleRootRedirect();
  }

  /**
   * If user hits "/", redirect them to the first permitted route (if any).
   * e.g. if `this.options[0] = "USERS"`, then "/" becomes "/user-management".
   */
  private handleRootRedirect() {
    if (this.currentUrl === '/' && this.options.length > 0) {
      const firstLabel = this.options[0];
      const targetRoute = this.mapLabelToRoute(firstLabel);
      if (targetRoute !== '/') {
        // Force redirect to that route
        this.router.navigateByUrl(targetRoute);
      }
    }
  }

  /**
   * Converts our label to the real route
   */
  private mapLabelToRoute(label: string): string {
    switch (label) {
      case 'EVENTS':         return '/events';
      case 'USERS':          return '/user-management';
      case 'ORGANIZATIONS':  return '/organizations';
      default:               return '/';
    }
  }

  /**
   * Utility for highlighting the current link
   */
  isActive(url: string): boolean {
    return this.currentUrl === url;
  }
}
